import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import type { FC } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { useFirstExitSignpost } from "./hooks/use-first-exit-signpost";
import type {
  PanicSituation,
  PanicSituationFirstExitDict,
  RoundaboutCurrentRole,
  RoundaboutRoomUser,
  RoundaboutTrackPendoEvent,
} from "./roundabout-markings";
import { getOtherEditors } from "./roundabout-utilities";

// This component can only be rendered when we first join
// it will be removed when the user has established themselves
export const RoundaboutFirstExit: FC<{
  setMyRole: (role: RoundaboutCurrentRole) => void;
  mySub: string;
  initialUsers: RoundaboutRoomUser[];
  trackPendoEvent: RoundaboutTrackPendoEvent;
}> = ({ setMyRole, mySub, initialUsers, trackPendoEvent }) => {
  const { handleClose, handleOpen, open, data } = useDialog<PanicSituation>();

  const handleReturnToSafety = useCallback(() => {
    handleClose();
    // try go back to the previous page
    try {
      window.history.back();
    } catch {
      // else go to home page
      window.location.href = "/";
    }
  }, [handleClose]);

  const handleEditHere = useCallback(() => {
    handleClose();
    setMyRole("Editing");
  }, [setMyRole, handleClose]);

  const handleViewHere = useCallback(() => {
    handleClose();
    setMyRole("Viewing");
  }, [setMyRole, handleClose]);

  const otherEditors = useMemo(() => getOtherEditors(initialUsers, mySub), [initialUsers, mySub]);
  const otherEditorsNames = otherEditors.map(({ nickname }) => nickname).join(", ");
  const otherEditorsDialogTitle = `${otherEditorsNames} ${
    otherEditors.length === 1 ? "is" : "are"
  } editing this Communication`;

  const panicSituationDict = useMemo<PanicSituationFirstExitDict>(
    () => ({
      "self-already-editing": {
        content: `You’re currently editing this Communication in a different window, which can result in conflicting edits. Would you like to edit it in this window instead?`,
        title: "You have this Communication open in another window",
        actions: [
          {
            label: "Go back",
            onClick: handleReturnToSafety,
          },
          {
            label: "Edit here",
            onClick: handleEditHere,
            variant: "contained",
          },
        ],
      },
      "others-already-editing": {
        content: `Any changes you make can overwrite their edits, and vice-versa. Do you want to continue?`,
        title: otherEditorsDialogTitle,
        actions: [
          {
            label: "Go back",
            onClick: handleReturnToSafety,
          },
          {
            label: "Continue to edit",
            onClick: handleEditHere,
            variant: "contained",
          },
          {
            label: "Open in read-only mode",
            onClick: handleViewHere,
          },
        ],
      },
    }),
    [handleReturnToSafety, handleEditHere, handleViewHere, otherEditorsDialogTitle],
  );

  const { panicSituation } = useFirstExitSignpost(mySub, initialUsers);

  useEffect(() => {
    if (panicSituation) {
      handleOpen(panicSituationDict[panicSituation]);
      trackPendoEvent(panicSituation + "-dialog");
    } else {
      // panicSituation can have a value at first render as useFirstExitSignpost is a usememo.
      // If the value is null then immediately set role which unmounts this component.
      // close makes it have a nice close effect until the render.
      setMyRole("Editing");
      handleClose();
    }
  }, [panicSituation, handleClose, handleOpen, panicSituationDict, trackPendoEvent, setMyRole]);

  return (
    <>
      <Dialog open={open && !!data}>
        <DialogTitle>{data?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{data?.content}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {data?.actions.map((action, index) => (
            <Button
              data-analytics-id={`moment-editor-clash-${panicSituation || ""}-${action.label}`}
              key={index}
              onClick={action.onClick}
              variant={action.variant}
            >
              {action.label}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
    </>
  );
};
